"use client"

import { useState } from "react"
import { ChevronRight, Link2, Check, Shield, CreditCard } from "lucide-react"
import Navbar from "@/app/Components/NavbarSection"
import Footer from "@/app/Sections/Footer"

const sections = [
    {
        id: "acceptance-of-terms",
        number: "01",
        title: "Acceptance of Terms",
        body: "By creating an account or purchasing services from Aurex Hosting, you agree to comply with and be bound by these Terms. If you do not agree with any part of these Terms, you may not use our services.",
    },
    {
        id: "eligibility",
        number: "02",
        title: "Eligibility and Age Restrictions",
        body: "You must be at least 13 years of age to use Aurex Hosting services. By agreeing to these Terms, you represent and warrant that you are 13 years of age or older. If you are between the ages of 13 and 18, you must have the permission and consent of a parent or legal guardian to use our services and agree to these Terms.",
    },
    {
        id: "refund-policy",
        number: "03",
        title: "Refund Policy",
        body: "Aurex Hosting offers a strict 24-hour refund policy. You may request a full refund within exactly 24 hours of your initial purchase or service activation. After the 24-hour window has passed, all sales are final, and no refunds, prorated or otherwise, will be issued for any reason. Refunds do not apply to customized services, dedicated IP addresses, or domain registrations.",
    },
    {
        id: "acceptable-use",
        number: "04",
        title: "Acceptable Use Policy (AUP)",
        body: "You agree to use our infrastructure responsibly. You may not use our services to host illegal content, malware, or pirated software; engage in activities that disrupt our networks (DoS/DDoS); run malicious scripts or miners; or send spam. Aurex Hosting reserves the right to immediately suspend or terminate your service without a refund if you violate this policy.",
    },
    {
        id: "account-security",
        number: "05",
        title: "Account Security and Responsibility",
        body: "You are solely responsible for maintaining the confidentiality of your account credentials, passwords, and billing information. Aurex Hosting is not liable for any loss or damage arising from unauthorized access to your account or servers.",
    },
    {
        id: "data-backups",
        number: "06",
        title: "Data and Backups",
        body: "While we maintain our infrastructure to the highest standards, you are solely responsible for backing up your own data. Aurex Hosting does not guarantee the retention of your data in the event of hardware failure, account suspension, or termination. We highly recommend configuring external, off-site backups for all your critical server data.",
    },
    {
        id: "service-availability",
        number: "07",
        title: "Service Availability (Uptime)",
        body: "Aurex Hosting strives to provide uninterrupted service; however, we do not guarantee 100% uptime. Services may occasionally be unavailable due to scheduled maintenance, hardware upgrades, or unforeseen circumstances beyond our control. We will make reasonable efforts to notify you in advance of any scheduled network or hardware migrations.",
    },
    {
        id: "limitation-of-liability",
        number: "08",
        title: "Limitation of Liability",
        body: "In no event shall Aurex Hosting, its staff, or its partners be liable for any indirect, incidental, special, or consequential damages, including but not limited to loss of data, loss of revenue, or business interruption arising out of the use or inability to use our services.",
    },
    {
        id: "modifications",
        number: "09",
        title: "Modifications to the Terms",
        body: "Aurex Hosting reserves the right to modify these Terms at any time. We will notify users of any significant changes via email or through announcements on our platform. Continued use of our services after such changes constitutes your acceptance of the new Terms.",
    },
    {
        id: "governing-law",
        number: "10",
        title: "Governing Law",
        body: "These Terms shall be governed by and construed in accordance with the laws of Sri Lanka. Any disputes arising from these terms will be subject to the exclusive jurisdiction of the applicable courts.",
    },
]

function CopyLinkButton({ id }: { id: string }) {
    const [copied, setCopied] = useState(false)
    const copy = () => {
        if (typeof window === "undefined") return
        navigator.clipboard.writeText(`${window.location.origin}${window.location.pathname}#${id}`)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }
    return (
        <button
            onClick={copy}
            aria-label="Copy section link"
            className="flex-shrink-0 p-1.5 rounded-md text-gray-600 hover:text-[#8B5CF6] hover:bg-[#8B5CF6]/10 transition-colors"
        >
            {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Link2 className="w-3.5 h-3.5" />}
        </button>
    )
}

export default function TermsOfServicePage() {
    return (
        <div className="bg-black">
            <Navbar />
            <div className="relative overflow-hidden">
                <div className="absolute inset-0 pointer-events-none">
                    <div
                        className="absolute inset-0"
                        style={{
                            backgroundImage: "url('https://cdn.aurex.lk/assets/hero-mc.webp')",
                            backgroundSize: "cover",
                            backgroundPosition: "center top",
                            filter: "blur(6px) brightness(0.45)",
                            transform: "scale(1.05)",
                        }}
                    />
                    <div className="absolute inset-0 bg-[#3b0d6e]/40" />
                    <div
                        className="absolute inset-0"
                        style={{ background: "radial-gradient(ellipse 90% 60% at 50% 110%, #000000 0%, transparent 90%)" }}
                    />
                    <div
                        className="absolute top-0 left-0 right-0 h-40 pointer-events-none"
                        style={{ background: "linear-gradient(to bottom, #000000, transparent)" }}
                    />
                </div>

                <div className="relative z-10">
                    <div className="max-w-6xl mx-auto px-4 sm:px-8 pt-12 pb-24">
                        <nav className="flex items-center gap-1.5 text-xs font-poppins text-gray-500 mb-8">
                            <a href="/" className="hover:text-white transition-colors">Home</a>
                            <ChevronRight className="w-3 h-3" />
                            <span className="text-gray-400">Legal</span>
                            <ChevronRight className="w-3 h-3" />
                            <span className="text-white">Terms of Use</span>
                        </nav>
                        <h1 className="font-akira text-3xl sm:text-4xl xl:text-5xl text-white leading-tight tracking-wide mb-3">
                            Terms of Use
                        </h1>
                        <p className="text-gray-500 font-poppins text-sm">Last updated: May 7, 2026</p>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 pointer-events-none" style={{ lineHeight: 0 }}>
                        <svg width="100%" height="50" viewBox="0 0 2598 150" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMaxYMax meet" style={{ display: "block", position: "relative", zIndex: 20 }}>
                            <path d="M2598 150H0L230.359 0H2598V150Z" fill="black" />
                        </svg>
                    </div>
                </div>
            </div>

            <div className="relative z-10 w-full bg-black">

                <div className="max-w-6xl mx-auto px-4 sm:px-8 py-12 relative">

                    <div className="flex flex-wrap gap-3 mb-10">
                        <a href="/privacy" className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/5 bg-white/5 hover:bg-[#8B5CF6]/10 hover:border-[#8B5CF6]/40 transition-all group">
                            <Shield className="w-4 h-4 text-[#8B5CF6]" />
                            <span className="text-sm font-poppins text-gray-300 group-hover:text-white transition-colors">Privacy Policy</span>
                            <ChevronRight className="w-3 h-3 text-gray-600 group-hover:text-[#8B5CF6] transition-colors" />
                        </a>
                        <a href="/refund-policy" className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/5 bg-white/5 hover:bg-[#8B5CF6]/10 hover:border-[#8B5CF6]/40 transition-all group">
                            <CreditCard className="w-4 h-4 text-[#8B5CF6]" />
                            <span className="text-sm font-poppins text-gray-300 group-hover:text-white transition-colors">Refund Policy</span>
                            <ChevronRight className="w-3 h-3 text-gray-600 group-hover:text-[#8B5CF6] transition-colors" />
                        </a>
                    </div>

                    <p className="text-gray-400 font-poppins text-sm leading-relaxed mb-10 border-l-4 border-[#8B5CF6] pl-4">
                        Welcome to Aurex Hosting. By accessing, registering for, or using our services, you agree to be bound by these Terms of Use (&quot;Terms&quot;). Please read them carefully before using our platform.
                    </p>

                    <div className="flex flex-col">
                        {sections.map((s, i) => (
                            <div key={s.id}>
                                <section id={s.id} className="py-8">
                                    <div className="flex items-start gap-3 mb-4">
                                        <span className="font-akira text-[#8B5CF6]/50 text-xs mt-1 flex-shrink-0 select-none">{s.number}</span>
                                        <h2 className="font-akira text-white text-base sm:text-lg leading-snug flex-1">{s.title}</h2>
                                        <CopyLinkButton id={s.id} />
                                    </div>
                                    <p className="text-gray-400 font-poppins text-sm leading-relaxed pl-7">{s.body}</p>
                                </section>
                                {i < sections.length - 1 && (
                                    <hr className="border-white/5" />
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="mt-10 pt-8 border-t border-white/5">
                        <p className="text-gray-600 font-poppins text-xs">
                            Questions about these terms?{" "}
                            <a href="mailto:legal@aurex.lk" className="text-[#8B5CF6] hover:text-purple-400 transition-colors">
                                legal@aurex.lk
                            </a>
                        </p>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    )
}
